import type { FC } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { mediaQueryMaxMedium } from "../../mediaQuery";
import { Link } from "../Link";
import { focusVisible } from "./SidebarItemElements";
import { SIDEBAR } from "./constants";
import { useSidebarState } from "./state";
import { type SubNavigationConfig } from "./type";
import { isSubItemActive } from "./utils";

const LEFT_PADDING =
  SIDEBAR.iconWidth + SIDEBAR.itemTextMargin + (SIDEBAR.basePadding * 2 + 4);

type SubMenuButtonProps = {
  isCollapsed: boolean;
  isActive: boolean;
};
export const SubMenuButton = styled(Link)<SubMenuButtonProps>`
  all: initial;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  text-decoration: none;
  color: var(--sinch-sys-color-text-default);
  border-radius: 8px;

  ${focusVisible("4px")}

  ${mediaQueryMaxMedium} {
    width: 100%;
  }

  ${({ isCollapsed, isActive }) =>
    isCollapsed
      ? css`
          margin: 4px 24px;
          padding: 10px 16px;
          width: auto;

          ${isActive &&
          css`
            background-color: var(--sinch-sys-color-container-main-active);
          `}
          &:hover {
            background-color: var(--sinch-sys-color-container-main-active);
          }
        `
      : css`
          padding: ${SIDEBAR.basePadding}px 0 ${SIDEBAR.basePadding}px
            ${LEFT_PADDING}px;
        `}
`;

export const SubMenuButtonText = styled.p<{
  isActive: boolean;
  isCollapsed: boolean;
}>`
  margin: 0;
  margin-right: auto;
  overflow: hidden;
  font: var(--sinch-sys-font-desktop-title-s);
  font-size: var(--sinch-ref-typography-font-size-14);
  color: var(--sinch-sys-color-text-default);
  line-height: var(--sinch-ref-typography-line-height-22);
  ${({ isActive, isCollapsed }) =>
    (!isActive || isCollapsed) &&
    css`
      font-weight: var(--sinch-ref-typography-font-weight-400);
      color: var(--sinch-ref-color-main-stormy-400);
    `};
`;

type SubMenuItemActiveIndicatorProps = {
  isActive: boolean;
};
export const SubMenuItemActiveIndicator = styled.div<SubMenuItemActiveIndicatorProps>`
  width: 4px;
  height: 75%;
  background: var(--sinch-sys-color-container-contrast-primary-foreground);
  border-radius: 1px;
  position: absolute;
  left: 52px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};

  ${SubMenuButton}:hover & {
    display: block;
    opacity: 1;
    background: var(--sinch-sys-color-container-contrast-secondary-hover);
  }
`;

export const SubMenuItemBadge = styled.div`
  /* The menu items arrow up icon is size 24px, this makes the badge align with that icon. */
  width: 24px;
  display: grid;
  place-content: center;
`;

type SidebarSubMenuItemProps = {
  id: string;
  subRoutes: SubNavigationConfig["subRoutes"];
  thisSubRoute: SubNavigationConfig["subRoutes"][number];
  onHover?: () => void;
};

export const SidebarSubMenuItem: FC<SidebarSubMenuItemProps> = ({
  id,
  onHover,
  thisSubRoute,
  subRoutes,
}) => {
  const sidebarState = useSidebarState();
  const location = useLocation();

  const isActive = isSubItemActive(
    thisSubRoute.path,
    location.pathname,
    subRoutes
  );

  return (
    <SubMenuButton
      isCollapsed={sidebarState.isCollapsed}
      isActive={isActive}
      tabIndex={sidebarState.openedSubMenu !== id ? 0 : -1}
      onMouseOver={onHover}
      to={thisSubRoute.href}
    >
      {!sidebarState.isCollapsed && (
        <SubMenuItemActiveIndicator isActive={isActive} />
      )}
      <SubMenuButtonText
        isActive={isActive}
        isCollapsed={sidebarState.isCollapsed}
      >
        {thisSubRoute.label}
      </SubMenuButtonText>
    </SubMenuButton>
  );
};
